import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Severity } from '@sentry/types';
import { useRouter } from 'next/router';
import * as Sentry from '@sentry/node';

import { signInEmailVerifyCode } from '../../../api/methods/auth';
import { ERROR_MESSAGE_GENERIC } from '../../../constants/common';
import { ERROR_NO_TOKEN_RECEIVED } from '../../../constants/auth';
import { setAccessTokenCookie } from '../../../helpers/cookies';
import { SIGN_IN_VERIFICATION_CODE_VALIDATION_SCHEMA } from '../../../constants/forms';
import { SignInVerificationCodeFormData } from '../../../types/forms';
import { Button, TextFieldForm } from '../../common';

type VerificationCodeFormProps = {
  email: string;
  nextUrl: string | null;
};

const VerificationCodeForm = ({
  email,
  nextUrl,
}: VerificationCodeFormProps) => {
  const [error, setError] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    formState,
  } = useForm<SignInVerificationCodeFormData>({
    resolver: yupResolver(SIGN_IN_VERIFICATION_CODE_VALIDATION_SCHEMA),
  });

  const router = useRouter();

  const onSubmit = handleSubmit(async ({ code }) => {
    await signInEmailVerifyCode(email, code)
      .then(({ data }) => {
        const accessToken = data.access_token;
        const expiresIn = data.expires_in;
        const tokenType = data.token_type;

        if (!accessToken || tokenType !== 'Bearer') {
          setError(true);
          Sentry.captureMessage(ERROR_NO_TOKEN_RECEIVED, Severity.Error);
          return;
        }

        setAccessTokenCookie(accessToken, expiresIn);

        router.push(nextUrl ? nextUrl : '/');
      })
      .catch((e) => {
        setError(true);
        Sentry.captureException(e);
      });
  });

  if (error) {
    return <span>{ERROR_MESSAGE_GENERIC}</span>;
  }

  return (
    <form onSubmit={onSubmit}>
      <TextFieldForm
        {...register('code')}
        type="text"
        placeholder="Verification code"
        inputMode="numeric"
        autoComplete="one-time-code"
        className="w-full mb-5"
        error={formState.errors.code}
      />

      <Button type="submit" className="w-full">
        {formState.isSubmitting ? 'Signing in...' : 'Sign in'}
      </Button>
    </form>
  );
};

export default VerificationCodeForm;
